/**
 * Orders Section Component
 * Full orders list with table, search, filters
 * Unified design matching Sales Orders page
 * Uses URL filter sync for bookmarkable/shareable filter state
 */

import { useState, useEffect, useCallback, useMemo, useRef, lazy, Suspense, type MutableRefObject } from 'react'
import { Capacitor } from '@capacitor/core'
import { Filesystem, Directory } from '@capacitor/filesystem'
import { Share } from '@capacitor/share'
import { useDispatch, useSelector, useUrlFilterSync, useIsRTL, useSearch, useNavigate, useDrawerNavigationOptional, useForm, zodResolver, z } from '@spcc/core'
import { useTranslation } from '@spcc/core'
import { exportToXlsx, isForbiddenError, type ExportColumn } from '@spcc/core'
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  PaginationControls,
  Spinner,
  useToast,
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@spcc/uikit'
import { selectIsAdmin } from '@spcc/domains/admin-context'
import { selectLocationDerivedFactoryContext } from '@spcc/domains/locations'
import { selectUserEmail, selectUserRoleType, isReadOnlyAdminRole } from '@spcc/domains/auth'

import { ordersActions } from '../../../store/orders.slice'
import {
  selectFilteredOrders,
  selectOrderStats,
  selectOrdersLoading,
  selectOrdersError,
  selectOrdersFilter,
  selectOrdersPagination,
  selectSelectedOrderIds,
  selectSelectedOrdersCount,
  selectIsAllOnPageSelected,
  selectIsSomeOnPageSelected,
  selectCanDownloadBulkPDF,
  selectBulkDownloading,
  selectBulkCancelling,
  selectCanBulkCancelSelected,
  selectLastBulkCancelResult,
  selectSelectedOrders,
  selectBulkForceUpdating,
  selectLastBulkForceUpdateResult,
  selectCanForceStatusUpdate,
  selectIsAllOnPageSelectedAnyStatus,
  selectIsSomeOnPageSelectedAnyStatus,
  selectVariantFilterOptions,
} from '../../../store/orders.selectors'
import { useOrdersReadable } from '../../../copilot/orders.readable'
import { useOrdersCopilotActions } from '../../../copilot/orders.actions'
import { useOrdersSuggestions } from '../../../copilot/orders.suggestions'
const OrderDetailDrawer = lazy(() => import('../../drawers/order-detail-drawer').then(m => ({ default: m.OrderDetailDrawer })))
const OrderPdfDetailDrawer = lazy(() => import('../../drawers/order-pdf-detail-drawer').then(m => ({ default: m.OrderPdfDetailDrawer })))
import { ForceStatusUpdateModal, type ForceStatusUpdatePayload } from '../../modals/force-status-update-modal'
import { ordersApi } from '../../../infrastructure/api/orders-api'
import type { OrderPdfDocumentKind } from '../../drawers/order-pdf-detail-drawer'
import type { Order, OrderStatus, OrdersFilter, OrdersSortBy, OrdersSortOrder } from '../../../domain/types'
import { ordersFilterConfig } from '../../../domain/filter-config'
import { ORDER_TABLE_COLUMNS, ORDER_TABLE_COLUMNS_STORAGE_KEY } from './constants'
import type { OrderTableColumnKey } from './types'
import { bagsToTons, formatLoadingDuration, formatProcessingToDeliveredDuration, formatTimeInRiyadh } from './utils'

import {
  OrdersHeader,
  OrdersStats,
  OrdersToolbarV2,
  OrdersTable,
  OrdersEmptyState,
} from './components'

type TranslateFn = ReturnType<typeof useTranslation>['t']
type ToastFn = ReturnType<typeof useToast>['toast']
type AppDispatch = ReturnType<typeof useDispatch>

interface OrderPdfContext {
  orderId: string
  orderNumber: string
  kind: OrderPdfDocumentKind
}

interface OrderPdfPreview {
  orderId: string
  orderNumber: string
  kind: OrderPdfDocumentKind
  fileName: string
  fileSize: string
  blob: Blob
  url: string
  fileUri?: string
  cacheFileName?: string
}

function getPdfLoadKey(orderId: string, kind: OrderPdfDocumentKind): string {
  return `${orderId}::${kind}`
}

interface PdfPreviewStateHandlers {
  setPdfContext: (context: OrderPdfContext | null) => void
  setPdfPreview: (preview: OrderPdfPreview | null) => void
  setPdfDrawerOpen: (open: boolean) => void
  setPdfLoading: (loading: boolean) => void
  setPdfError: (error: string | null) => void
}

interface OrderExportEmailFormValues {
  email: string
}

// ====================================
// External Functions (Event Handlers)
// ====================================
function createOrderExportEmailSchema(t: TranslateFn) {
  return z.object({
    email: z
      .string()
      .trim()
      .min(1, t('orders.export.emailRequired', { defaultValue: 'Email is required' }))
      .email(t('orders.export.emailInvalid', { defaultValue: 'Please enter a valid email address' })),
  })
}

function getInitialOrderExportEmail(userEmail: string | null): string {
  return userEmail?.trim() ?? ''
}

function getOrderExportColumns(isAdmin: boolean): ExportColumn<Order>[] {
  const columns: ExportColumn<Order>[] = [
    { header: 'Order #', key: 'orderNumber', width: 18 },
    { header: 'SAP Sales Order #', key: 'sapSalesOrderNumber', width: 20 },
    { header: 'Status', key: 'status', width: 14 },
    { header: 'Order Date', key: 'orderDate', width: 14 },
    { header: 'Enter Time', accessor: (order) => formatTimeInRiyadh(order.processingStartedAt), width: 20 },
    { header: 'Delivery Time', accessor: (order) => formatTimeInRiyadh(order.deliveryAt), width: 20 },
    { header: 'Processing to Delivered', accessor: (order) => formatProcessingToDeliveredDuration(order.processingToDeliveredSeconds), width: 22 },
    { header: 'Loading Time', accessor: (order) => formatLoadingDuration(order.loadingDurationSeconds), width: 16 },
    { header: 'Product', accessor: (order) => order.items.map((item) => item.product.en).join(', '), width: 28 },
    { header: 'Quantity', accessor: (order) => order.items.map((item) => `${item.quantity.value} ${item.quantity.unit}`).join(', '), width: 16 },
    {
      header: 'Planned Qty (tons)',
      accessor: (order) => {
        const item = order.items[0]
        if (!item) return ''
        if (item.quantity.unit === 'tons') return item.quantity.value
        if (item.quantity.unit === 'bags') {
          const tons = bagsToTons(item.quantity.value, item.bagWeightKg)
          return tons ?? ''
        }
        return ''
      },
      width: 18,
    },
    {
      header: 'Delivered Qty (tons)',
      accessor: (order) => order.deliveredQtyTons ?? '',
      width: 20,
    },
    { header: isAdmin ? 'Sub-Customer' : 'Customer', accessor: (order) => order.subCustomerName ?? '', width: 20 },
    { header: isAdmin ? 'Sub-Customer Code' : 'Customer Code', accessor: (order) => order.subCustomerCode ?? '', width: 18 },
    { header: 'Driver', key: 'driverName', width: 18 },
    { header: 'Vehicle', key: 'vehicleLicensePlate', width: 16 },
    { header: 'City', accessor: (order) => order.dropoffCity ?? '', width: 18 },
    { header: 'Loading Location', accessor: (order) => order.loadingLocationLabel ?? '', width: 20 },
  ]

  if (isAdmin) {
    columns.unshift({ header: 'Customer', accessor: (order) => order.tenant?.name ?? '', width: 20 })
  }

  return columns
}

function getExportTargetDescription(
  t: TranslateFn,
  selectedCount: number
): string {
  if (selectedCount > 0) {
    return t('orders.export.exportingSelected', {
      count: selectedCount,
      defaultValue: `Exporting ${selectedCount} selected order(s)`,
    })
  }

  return t('orders.export.exportingDesc', {
    defaultValue: 'Fetching all records',
  })
}

function replaceOrderNumberPlaceholder(text: string, orderNumber: string): string {
  return text.replace(/{{\s*orderNumber\s*}}/g, orderNumber)
}

function getDownloadingPDFDescription(t: TranslateFn, orderNumber: string): string {
  const translated = t('orders.downloadingPDFDesc', { orderNumber })
  return replaceOrderNumberPlaceholder(translated, orderNumber)
}

function getPdfDownloadedDescription(t: TranslateFn, orderNumber: string): string {
  const translated = t('orders.pdfDownloadedDesc', { orderNumber })
  return replaceOrderNumberPlaceholder(translated, orderNumber)
}

function getDownloadingDeliveryNoteDescription(t: TranslateFn, orderNumber: string): string {
  const translated = t('orders.downloadingDeliveryNoteDesc', { orderNumber })
  return replaceOrderNumberPlaceholder(translated, orderNumber)
}

function getDeliveryNoteDownloadedDescription(t: TranslateFn, orderNumber: string): string {
  const translated = t('orders.deliveryNoteDownloadedDesc', { orderNumber })
  return replaceOrderNumberPlaceholder(translated, orderNumber)
}

function getCancelSuccessDescription(t: TranslateFn, orderNumber: string): string {
  const translated = t('orders.cancelSuccessDesc', { orderNumber })
  return replaceOrderNumberPlaceholder(translated, orderNumber)
}

function getCancelErrorDescription(t: TranslateFn, orderNumber: string): string {
  const translated = t('orders.cancelErrorDesc', { orderNumber })
  return replaceOrderNumberPlaceholder(translated, orderNumber)
}

function getResolvedCancelErrorDescription(
  t: TranslateFn,
  error: string,
  orderNumber: string
): string {
  const translated = t(error, { defaultValue: '' }).trim()
  if (translated !== '' && translated !== error) {
    return translated
  }
  return getCancelErrorDescription(t, orderNumber)
}

function getOrderNumberDisplay(order: Order): string {
  const trimmed = order.orderNumber.trim()
  return trimmed !== '' ? trimmed : order.id
}

function getOrderNumberForDownload(order: Order): string | undefined {
  const trimmed = order.orderNumber.trim()
  return trimmed !== '' ? trimmed : undefined
}

function isCancelableOrder(order: Order, roleType?: string): boolean {
  if (roleType === 'packhouse_manager') return false
  const sapOrderNumber = order.sapSalesOrderNumber?.trim()
  return order.status === 'pending' && !sapOrderNumber
}

function formatFileSize(bytes: number, t: TranslateFn): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return `0 ${t('orders.pdfFileSizeUnitKB')}`
  const units = [
    t('orders.pdfFileSizeUnitB'),
    t('orders.pdfFileSizeUnitKB'),
    t('orders.pdfFileSizeUnitMB'),
    t('orders.pdfFileSizeUnitGB'),
  ]
  let size = bytes
  let unitIndex = 0
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024
    unitIndex += 1
  }
  const precision = size >= 10 || unitIndex === 0 ? 0 : 1
  return `${size.toFixed(precision)} ${units[unitIndex]}`
}

function createWebPdfPreview(
  payload: {
    orderId: string
    orderNumber: string
    blob: Blob
    filename: string
  },
  t: TranslateFn,
  kind: OrderPdfDocumentKind,
): OrderPdfPreview {
  const url = URL.createObjectURL(payload.blob)
  return {
    orderId: payload.orderId,
    orderNumber: payload.orderNumber,
    kind,
    fileName: payload.filename,
    fileSize: formatFileSize(payload.blob.size, t),
    blob: payload.blob,
    url,
  }
}

function getNativePreviewUrl(fileUri: string): string {
  if (typeof Capacitor?.convertFileSrc === 'function') {
    return Capacitor.convertFileSrc(fileUri)
  }
  return fileUri
}

function isBlobUrl(url?: string): url is string {
  return typeof url === 'string' && url.startsWith('blob:')
}

function revokePdfUrl(url?: string): void {
  if (!isBlobUrl(url)) return
  URL.revokeObjectURL(url)
}

function setPdfPreviewState(
  current: OrderPdfPreview | null,
  next: OrderPdfPreview | null,
  setPreview: (preview: OrderPdfPreview | null) => void
): void {
  if (current?.cacheFileName) {
    void deletePdfFromCache(current.cacheFileName)
  }
  revokePdfUrl(current?.url)
  setPreview(next)
}

function resetPdfPreviewState(
  current: OrderPdfPreview | null,
  handlers: PdfPreviewStateHandlers,
  options?: { preservePreview?: boolean }
): void {
  if (!options?.preservePreview) {
    setPdfPreviewState(current, null, handlers.setPdfPreview)
  }
  handlers.setPdfContext(null)
  handlers.setPdfError(null)
  handlers.setPdfLoading(false)
}

function isNativePlatform(): boolean {
  return typeof Capacitor?.isNativePlatform === 'function' ? Capacitor.isNativePlatform() : false
}

function getCapacitorPlatform(): string {
  if (typeof Capacitor?.getPlatform === 'function') {
    return Capacitor.getPlatform()
  }
  return 'web'
}

type PdfPreviewMode = 'iframe' | 'pdfjs'

function getPdfPreviewMode(isNative: boolean): PdfPreviewMode {
  // Native WebViews cannot reliably load PDFs via iframe (Android) or via
  // Capacitor.convertFileSrc blob URLs on iOS (LSApplicationWorkspaceErrorDomain).
  // pdfjs renders from the in-memory blob like gate/customer native flows.
  if (isNative) return 'pdfjs'
  return 'iframe'
}

function shouldUsePdfPreview(isNative: boolean): boolean {
  return isNative
}

function normalizeShareUri(uri: string): string {
  if (uri.startsWith('file://') || uri.startsWith('content://') || uri.startsWith('http')) {
    return uri
  }
  if (uri.startsWith('/')) {
    return `file://${uri}`
  }
  return uri
}

function isFileShareUri(uri: string): boolean {
  return uri.startsWith('file://')
}

function isContentShareUri(uri: string): boolean {
  return uri.startsWith('content://')
}

function buildNativeShareOptions(params: {
  title: string
  text?: string
  uri: string
  platform: string
}): { title: string; text?: string; url?: string; files?: string[] } {
  const { title, text, uri, platform } = params
  const normalized = normalizeShareUri(uri)
  if (platform === 'android' && isFileShareUri(normalized)) {
    return { title, files: [normalized] }
  }

  const options: { title: string; text?: string; url?: string } = { title, url: normalized }
  if (text) {
    options.text = text
  }
  return options
}

function sanitizeShareFilename(name: string): string {
  return name.replace(/[\\/:*?"<>|]+/g, '_').replace(/\s+/g, ' ').trim()
}

function ensurePdfFilename(name: string): string {
  const trimmed = name.trim()
  if (!trimmed) return 'order.pdf'
  return trimmed.toLowerCase().endsWith('.pdf') ? trimmed : `${trimmed}.pdf`
}

function getSafePdfFilename(name: string): string {
  return ensurePdfFilename(sanitizeShareFilename(name))
}

function readBlobAsBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      if (typeof reader.result !== 'string') {
        reject(new Error('Failed to read PDF'))
        return
      }
      const split = reader.result.split(',')
      resolve(split.length > 1 ? split[1] : reader.result)
    }
    reader.onerror = () => reject(reader.error ?? new Error('Failed to read PDF'))
    reader.readAsDataURL(blob)
  })
}

async function writePdfToCache(base64: string, filename: string): Promise<string> {
  const result = await Filesystem.writeFile({
    path: filename,
    data: base64,
    directory: Directory.Cache,
    recursive: true,
  })
  return result.uri
}

async function deletePdfFromCache(filename: string): Promise<void> {
  try {
    await Filesystem.deleteFile({
      directory: Directory.Cache,
      path: filename,
    })
  } catch {
    // Ignore cleanup errors
  }
}

async function writePdfToDirectory(base64: string, filename: string, directory: Directory): Promise<string> {
  const result = await Filesystem.writeFile({
    path: filename,
    data: base64,
    directory,
    recursive: true,
  })
  return result.uri
}

async function resolveShareUri(directory: Directory, filename: string, uri: string): Promise<string> {
  const normalized = normalizeShareUri(uri)
  if (isFileShareUri(normalized)) return normalized

  try {
    const uriResult = await Filesystem.getUri({
      directory,
      path: filename,
    })
    const resolved = normalizeShareUri(uriResult.uri)
    if (isFileShareUri(resolved)) return resolved
  } catch {
    // Ignore and fall through
  }

  try {
    const statResult = await Filesystem.stat({
      directory,
      path: filename,
    })
    const resolved = normalizeShareUri(statResult.uri)
    if (isFileShareUri(resolved)) return resolved
  } catch {
    // Ignore and fall through
  }

  return normalized
}

function getShareDirectories(platform: string): Directory[] {
  if (platform === 'android') {
    return [Directory.Cache, Directory.Data, Directory.ExternalCache, Directory.External]
  }
  return [Directory.Cache, Directory.Documents]
}

function logShareDirectoryError(directory: Directory, error: unknown): void {
  console.warn(`[OrdersSection] Failed to write share file (${directory})`, error)
}

async function createShareableFile(params: {
  base64: string
  filename: string
  platform: string
}): Promise<{ uri: string }> {
  const { base64, filename, platform } = params
  const directories = getShareDirectories(platform)

  for (const directory of directories) {
    try {
      const uri = await writePdfToDirectory(base64, filename, directory)
      const resolved = await resolveShareUri(directory, filename, uri)
      if (isFileShareUri(resolved)) {
        return { uri: resolved }
      }
    } catch (error) {
      logShareDirectoryError(directory, error)
    }
  }

  throw new Error('No shareable file uri')
}

function canUseWebShare(): boolean {
  return typeof navigator !== 'undefined' && typeof navigator.share === 'function'
}

async function createNativePdfPreview(
  payload: {
    orderId: string
    orderNumber: string
    blob: Blob
    filename: string
  },
  t: TranslateFn,
  kind: OrderPdfDocumentKind,
): Promise<OrderPdfPreview> {
  const safeFilename = getSafePdfFilename(payload.filename)
  const base64 = await readBlobAsBase64(payload.blob)
  const fileUri = await writePdfToCache(base64, safeFilename)
  return {
    orderId: payload.orderId,
    orderNumber: payload.orderNumber,
    kind,
    fileName: safeFilename,
    fileSize: formatFileSize(payload.blob.size, t),
    blob: payload.blob,
    url: getNativePreviewUrl(fileUri),
    fileUri,
    cacheFileName: safeFilename,
  }
}

async function buildPdfPreview(
  payload: {
    orderId: string
    orderNumber: string
    blob: Blob
    filename: string
  },
  t: TranslateFn,
  isNative: boolean,
  kind: OrderPdfDocumentKind,
): Promise<OrderPdfPreview> {
  if (!isNative) {
    return createWebPdfPreview(payload, t, kind)
  }

  try {
    return await createNativePdfPreview(payload, t, kind)
  } catch (error) {
    console.error('[OrdersSection] Failed to cache PDF for preview:', error)
    return createWebPdfPreview(payload, t, kind)
  }
}

async function sharePdfBlob(params: {
  blob: Blob
  filename: string
  title: string
  text?: string
  t: TranslateFn
  toast: ToastFn
  fileUri?: string
  shareErrorDescription?: string
}): Promise<boolean> {
  const { blob, filename, title, text, t, toast, fileUri, shareErrorDescription } = params

  const nativeShare = isNativePlatform()
  const webShare = canUseWebShare()
  const platform = getCapacitorPlatform()

  if (!nativeShare && !webShare) {
    toast({
      title: t('common.error'),
      description: t('orders.pdfShareUnavailableDesc'),
      variant: 'destructive',
    })
    return false
  }

  try {
    const safeFilename = getSafePdfFilename(filename)

    if (nativeShare) {
      const normalizedUri = fileUri ? normalizeShareUri(fileUri) : null
      const isShareableUri = normalizedUri
        ? (platform === 'android' ? isFileShareUri(normalizedUri) : (isFileShareUri(normalizedUri) || isContentShareUri(normalizedUri)))
        : false
      const shareableFile = isShareableUri
        ? { uri: normalizedUri as string, directory: Directory.Cache, cleanup: false }
        : null

      let shareUri = shareableFile?.uri

      if (!shareUri) {
        const base64 = await readBlobAsBase64(blob)
        const created = await createShareableFile({
          base64,
          filename: safeFilename,
          platform,
        })
        shareUri = created.uri
      }

      const shareOptions = buildNativeShareOptions({
        title,
        text,
        uri: shareUri,
        platform,
      })

      await Share.share(shareOptions)
      return true
    }

    const file = new File([blob], safeFilename, { type: 'application/pdf' })
    if (typeof navigator.canShare === 'function' && !navigator.canShare({ files: [file] })) {
      throw new Error('Share not supported')
    }
    await navigator.share({
      title,
      files: [file],
    })
    return true
  } catch (error) {
    console.error('[OrdersSection] Error sharing PDF:', error)
    const message = error instanceof Error ? error.message : ''
    const normalizedMessage = message.toLowerCase()
    const isUnavailable = normalizedMessage.includes('not available')
      || normalizedMessage.includes('no activity')
      || normalizedMessage.includes('share not supported')
    toast({
      title: t('common.error'),
      description: isUnavailable
        ? t('orders.pdfShareUnavailableDesc')
        : (shareErrorDescription ?? t('orders.pdfShareErrorDesc')),
      variant: 'destructive',
    })
    return false
  }
}

async function sharePdfPreview(params: {
  preview: OrderPdfPreview | null
  t: TranslateFn
  toast: ToastFn
}): Promise<void> {
  const { preview, t, toast } = params
  if (!preview) return

  const title = preview.kind === 'delivery_note'
    ? t('orders.deliveryNotePreviewTitle')
    : t('orders.pdfPreviewTitle')
  const shareErrorDescription = preview.kind === 'delivery_note'
    ? t('orders.deliveryNoteShareErrorDesc')
    : undefined

  await sharePdfBlob({
    blob: preview.blob,
    filename: preview.fileName,
    title,
    text: preview.orderNumber,
    t,
    toast,
    fileUri: preview.fileUri,
    shareErrorDescription,
  })
}

async function downloadOrderPdf(params: {
  order: Order
  t: TranslateFn
  toast: ToastFn
}): Promise<void> {
  const { order, t, toast } = params
  try {
    const displayOrderNumber = getOrderNumberDisplay(order)
    const downloadOrderNumber = getOrderNumberForDownload(order)

    toast({
      title: t('orders.downloadingPDF'),
      description: getDownloadingPDFDescription(t, displayOrderNumber),
    })

    await ordersApi.downloadOrderPDF(order.id, downloadOrderNumber)

    toast({
      title: t('orders.pdfDownloaded'),
      description: getPdfDownloadedDescription(t, displayOrderNumber),
    })
  } catch (error) {
    console.error('[OrdersSection] Error downloading PDF:', error)
    toast({
      title: t('orders.pdfDownloadError'),
      description: t('orders.pdfDownloadErrorDesc'),
      variant: 'destructive',
    })
  }
}

async function downloadOrderDeliveryNote(params: {
  order: Order
  t: TranslateFn
  toast: ToastFn
}): Promise<void> {
  const { order, t, toast } = params
  try {
    const displayOrderNumber = getOrderNumberDisplay(order)
    const downloadOrderNumber = getOrderNumberForDownload(order)

    toast({
      title: t('orders.downloadingDeliveryNote'),
      description: getDownloadingDeliveryNoteDescription(t, displayOrderNumber),
    })

    await ordersApi.downloadOrderDeliveryNote(order.id, downloadOrderNumber)

    toast({
      title: t('orders.deliveryNoteDownloaded'),
      description: getDeliveryNoteDownloadedDescription(t, displayOrderNumber),
    })
  } catch (error) {
    console.error('[OrdersSection] Error downloading delivery note:', error)
    toast({
      title: t('orders.deliveryNoteDownloadError'),
      description: t('orders.deliveryNoteDownloadErrorDesc'),
      variant: 'destructive',
    })
  }
}

async function downloadBulkOrderPdfs(params: {
  selectedOrderIds: string[]
  usePdfPreview: boolean
  t: TranslateFn
  toast: ToastFn
  dispatch: AppDispatch
}): Promise<void> {
  const { selectedOrderIds, usePdfPreview, t, toast, dispatch } = params
  if (selectedOrderIds.length === 0) return

  try {
    dispatch(ordersActions.setBulkDownloading(true))

    toast({
      title: t('orders.bulkActions.downloadingPDFs'),
      description: t('orders.bulkActions.preparingPDFs', { count: selectedOrderIds.length }),
    })

    if (usePdfPreview) {
      const payload = await ordersApi.getMultipleOrdersPDFPayload(selectedOrderIds)
      const shared = await sharePdfBlob({
        blob: payload.blob,
        filename: payload.filename,
        title: t('orders.pdfPreviewTitle'),
        t,
        toast,
      })
      if (!shared) {
        return
      }
    } else {
      await ordersApi.downloadMultipleOrdersPDF(selectedOrderIds)
    }

    toast({
      title: t('orders.bulkActions.pdfsDownloaded'),
      description: t('orders.bulkActions.pdfsDownloadedDesc', { count: selectedOrderIds.length }),
    })

    dispatch(ordersActions.clearSelection())
  } catch (error) {
    console.error('[OrdersSection] Error downloading PDFs:', error)
    toast({
      title: t('orders.pdfDownloadError'),
      description: t('orders.bulkActions.pdfDownloadErrorDesc'),
      variant: 'destructive',
    })
  } finally {
    dispatch(ordersActions.setBulkDownloading(false))
  }
}

async function openOrderPdfPreview(params: {
  order: Order
  t: TranslateFn
  toast: ToastFn
  currentPreview: OrderPdfPreview | null
  currentContext: OrderPdfContext | null
  currentError: string | null
  isLoading: boolean
  pendingLoadKeyRef: MutableRefObject<string | null>
  requestRef: MutableRefObject<number>
  handlers: PdfPreviewStateHandlers
}): Promise<void> {
  const {
    order,
    t,
    toast,
    currentPreview,
    currentContext,
    currentError,
    isLoading,
    pendingLoadKeyRef,
    requestRef,
    handlers,
  } = params
  const displayOrderNumber = getOrderNumberDisplay(order)
  const downloadOrderNumber = getOrderNumberForDownload(order)
  const documentKind: OrderPdfDocumentKind = 'order_confirmation'
  const loadKey = getPdfLoadKey(order.id, documentKind)

  handlers.setPdfContext({
    orderId: order.id,
    orderNumber: displayOrderNumber,
    kind: documentKind,
  })
  handlers.setPdfDrawerOpen(true)

  if (shouldReusePdfPreview({
    orderId: order.id,
    documentKind,
    currentPreview,
    currentContext,
    currentError,
    isLoading,
    pendingLoadKey: pendingLoadKeyRef.current,
  })) {
    if (!isLoading) {
      handlers.setPdfLoading(false)
    }
    return
  }

  pendingLoadKeyRef.current = loadKey
  const requestId = nextPdfRequestId(requestRef)
  setPdfPreviewState(currentPreview, null, handlers.setPdfPreview)
  handlers.setPdfLoading(true)
  handlers.setPdfError(null)

  toast({
    title: t('orders.downloadingPDF'),
    description: getDownloadingPDFDescription(t, displayOrderNumber),
  })

  try {
    const payload = await ordersApi.getOrderPDFPayload(order.id, downloadOrderNumber)
    if (!isLatestPdfRequest(requestRef, requestId)) return
    const preview = await buildPdfPreview(
      {
        orderId: order.id,
        orderNumber: displayOrderNumber,
        blob: payload.blob,
        filename: payload.filename,
      },
      t,
      isNativePlatform(),
      documentKind,
    )
    if (!isLatestPdfRequest(requestRef, requestId)) return
    setPdfPreviewState(currentPreview, preview, handlers.setPdfPreview)
    toast({
      title: t('orders.pdfPreviewReady'),
      description: t('orders.pdfPreviewReadyDesc'),
    })
  } catch (error) {
    if (!isLatestPdfRequest(requestRef, requestId)) return
    console.error('[OrdersSection] Error preparing PDF preview:', error)
    handlers.setPdfError(t('orders.pdfDownloadErrorDesc'))
    toast({
      title: t('orders.pdfDownloadError'),
      description: t('orders.pdfDownloadErrorDesc'),
      variant: 'destructive',
    })
  } finally {
    if (isLatestPdfRequest(requestRef, requestId)) {
      handlers.setPdfLoading(false)
      pendingLoadKeyRef.current = null
    }
  }
}

async function openOrderDeliveryNotePreview(params: {
  order: Order
  t: TranslateFn
  toast: ToastFn
  currentPreview: OrderPdfPreview | null
  currentContext: OrderPdfContext | null
  currentError: string | null
  isLoading: boolean
  pendingLoadKeyRef: MutableRefObject<string | null>
  requestRef: MutableRefObject<number>
  handlers: PdfPreviewStateHandlers
}): Promise<void> {
  const {
    order,
    t,
    toast,
    currentPreview,
    currentContext,
    currentError,
    isLoading,
    pendingLoadKeyRef,
    requestRef,
    handlers,
  } = params
  const displayOrderNumber = getOrderNumberDisplay(order)
  const downloadOrderNumber = getOrderNumberForDownload(order)
  const documentKind: OrderPdfDocumentKind = 'delivery_note'
  const loadKey = getPdfLoadKey(order.id, documentKind)

  handlers.setPdfContext({
    orderId: order.id,
    orderNumber: displayOrderNumber,
    kind: documentKind,
  })
  handlers.setPdfDrawerOpen(true)

  if (shouldReusePdfPreview({
    orderId: order.id,
    documentKind,
    currentPreview,
    currentContext,
    currentError,
    isLoading,
    pendingLoadKey: pendingLoadKeyRef.current,
  })) {
    if (!isLoading) {
      handlers.setPdfLoading(false)
    }
    return
  }

  pendingLoadKeyRef.current = loadKey
  const requestId = nextPdfRequestId(requestRef)
  setPdfPreviewState(currentPreview, null, handlers.setPdfPreview)
  handlers.setPdfLoading(true)
  handlers.setPdfError(null)

  toast({
    title: t('orders.downloadingDeliveryNote'),
    description: getDownloadingDeliveryNoteDescription(t, displayOrderNumber),
  })

  try {
    const payload = await ordersApi.getOrderDeliveryNotePayload(order.id, downloadOrderNumber)
    if (!isLatestPdfRequest(requestRef, requestId)) return
    const preview = await buildPdfPreview(
      {
        orderId: order.id,
        orderNumber: displayOrderNumber,
        blob: payload.blob,
        filename: payload.filename,
      },
      t,
      isNativePlatform(),
      documentKind,
    )
    if (!isLatestPdfRequest(requestRef, requestId)) return
    setPdfPreviewState(currentPreview, preview, handlers.setPdfPreview)
    toast({
      title: t('orders.deliveryNotePreviewReady'),
      description: t('orders.deliveryNotePreviewReadyDesc'),
    })
  } catch (error) {
    if (!isLatestPdfRequest(requestRef, requestId)) return
    console.error('[OrdersSection] Error preparing delivery note preview:', error)
    handlers.setPdfError(t('orders.deliveryNoteDownloadErrorDesc'))
    toast({
      title: t('orders.deliveryNoteDownloadError'),
      description: t('orders.deliveryNoteDownloadErrorDesc'),
      variant: 'destructive',
    })
  } finally {
    if (isLatestPdfRequest(requestRef, requestId)) {
      handlers.setPdfLoading(false)
      pendingLoadKeyRef.current = null
    }
  }
}

function shouldReusePdfPreview(params: {
  orderId: string
  documentKind: OrderPdfDocumentKind
  currentPreview: OrderPdfPreview | null
  currentContext: OrderPdfContext | null
  currentError: string | null
  isLoading: boolean
  pendingLoadKey: string | null
}): boolean {
  const {
    orderId,
    documentKind,
    currentPreview,
    currentContext,
    currentError,
    isLoading,
    pendingLoadKey,
  } = params
  const key = getPdfLoadKey(orderId, documentKind)
  if (pendingLoadKey === key) return true
  if (
    isLoading
    && currentContext?.orderId === orderId
    && currentContext.kind === documentKind
  ) {
    return true
  }
  if (currentError) return false
  return currentPreview?.orderId === orderId && currentPreview.kind === documentKind
}

function nextPdfRequestId(ref: MutableRefObject<number>): number {
  const next = ref.current + 1
  ref.current = next
  return next
}

function isLatestPdfRequest(ref: MutableRefObject<number>, requestId: number): boolean {
  return ref.current === requestId
}

function setPdfPreviewError(params: {
  message?: string
  handlers: PdfPreviewStateHandlers
  t: TranslateFn
}): void {
  const { message, handlers, t } = params
  const resolved = message && message.trim() ? message : t('orders.pdfDownloadErrorDesc')
  handlers.setPdfError(resolved)
}

const ORDER_TABLE_COLUMN_KEY_SET = new Set<OrderTableColumnKey>(
  ORDER_TABLE_COLUMNS.map((column) => column.key)
)

function getAvailableOrderTableColumns(isAdmin: boolean) {
  return ORDER_TABLE_COLUMNS.filter((column) => !column.requiresAdmin || isAdmin)
}

function getDefaultVisibleColumns(isAdmin: boolean): OrderTableColumnKey[] {
  return getAvailableOrderTableColumns(isAdmin).map((column) => column.key)
}

function isOrderTableColumnKey(value: unknown): value is OrderTableColumnKey {
  return typeof value === 'string' && ORDER_TABLE_COLUMN_KEY_SET.has(value as OrderTableColumnKey)
}

function readStoredVisibleColumns(): OrderTableColumnKey[] | null {
  if (typeof window === 'undefined') return null

  try {
    const stored = window.localStorage.getItem(ORDER_TABLE_COLUMNS_STORAGE_KEY)
    if (!stored) return null

    const parsed: unknown = JSON.parse(stored)
    if (!Array.isArray(parsed)) return null

    const columns = parsed.filter(isOrderTableColumnKey)
    return columns.length > 0 ? columns : null
  } catch (error) {
    console.warn('[OrdersSection] Failed to parse table columns settings', error)
    return null
  }
}

function writeStoredVisibleColumns(columns: OrderTableColumnKey[]): void {
  if (typeof window === 'undefined') return

  try {
    window.localStorage.setItem(ORDER_TABLE_COLUMNS_STORAGE_KEY, JSON.stringify(columns))
  } catch (error) {
    console.warn('[OrdersSection] Failed to persist table columns settings', error)
  }
}

function normalizeVisibleColumns(columns: OrderTableColumnKey[], isAdmin: boolean): OrderTableColumnKey[] {
  const availableColumns = getAvailableOrderTableColumns(isAdmin)
  const availableColumnKeySet = new Set(availableColumns.map((column) => column.key))
  const nextVisibleColumnsSet = new Set(columns.filter((column) => availableColumnKeySet.has(column)))

  for (const column of availableColumns) {
    if (column.locked) {
      nextVisibleColumnsSet.add(column.key)
    }
  }

  const normalized = availableColumns
    .map((column) => column.key)
    .filter((column) => nextVisibleColumnsSet.has(column))

  return normalized.length > 0 ? normalized : getDefaultVisibleColumns(isAdmin)
}

function getInitialVisibleColumns(isAdmin: boolean): OrderTableColumnKey[] {
  const storedColumns = readStoredVisibleColumns()
  if (!storedColumns) return getDefaultVisibleColumns(isAdmin)
  return normalizeVisibleColumns(storedColumns, isAdmin)
}

function buildToolbarColumnOptions(columns: ReturnType<typeof getAvailableOrderTableColumns>) {
  return columns.map((column) => ({
    key: column.key,
    labelKey: column.labelKey,
    locked: column.locked,
  }))
}

function hasOrderBeenCancelled(orders: Order[], orderId: string): boolean {
  const order = orders.find((item) => item.id === orderId)
  return order?.status === 'cancelled'
}

function getSelectableOrderIds(orders: Order[]): string[] {
  return orders
    .filter((order) => order.status === 'pending')
    .map((order) => order.id)
}

function clearPendingCancelState(params: {
  pendingCancelOrderIdRef: MutableRefObject<string | null>
  pendingCancelOrderNumberRef: MutableRefObject<string | null>
}): void {
  params.pendingCancelOrderIdRef.current = null
  params.pendingCancelOrderNumberRef.current = null
}

// ====================================
// Component
// ====================================
interface OrdersSectionProps {
  /** Portal self-service queue join, forwarded to the order drawer */
  canJoinQueue?: boolean
  onJoinQueue?: (order: Order) => Promise<void>
}

export function OrdersSection({ canJoinQueue, onJoinQueue }: OrdersSectionProps = {}) {
  const dispatch = useDispatch()
  const { t, i18n } = useTranslation()
  const isRTL = useIsRTL()
  const { toast } = useToast()
  const drawerNav = useDrawerNavigationOptional()

  // Redux selectors
  const orders = useSelector(selectFilteredOrders)
  const stats = useSelector(selectOrderStats)
  const loading = useSelector(selectOrdersLoading)
  const error = useSelector(selectOrdersError)
  const filter = useSelector(selectOrdersFilter)
  const pagination = useSelector(selectOrdersPagination)
  const locationsContext = useSelector(selectLocationDerivedFactoryContext)
  const isAdmin = useSelector(selectIsAdmin)
  const variantFilterOptions = useSelector(selectVariantFilterOptions)
  const userEmail = useSelector(selectUserEmail)
  const availableColumns = useMemo(
    () => getAvailableOrderTableColumns(isAdmin),
    [isAdmin]
  )
  const toolbarColumnOptions = useMemo(
    () => buildToolbarColumnOptions(availableColumns),
    [availableColumns]
  )

  // Multi-select selectors
  const selectedOrderIds = useSelector(selectSelectedOrderIds)
  const selectedCount = useSelector(selectSelectedOrdersCount)
  const isAllOnPageSelected = useSelector(selectIsAllOnPageSelected)
  const isSomeOnPageSelected = useSelector(selectIsSomeOnPageSelected)
  const canDownloadBulkPDF = useSelector(selectCanDownloadBulkPDF)
  const bulkDownloading = useSelector(selectBulkDownloading)
  const bulkCancelling = useSelector(selectBulkCancelling)
  const canBulkCancelSelected = useSelector(selectCanBulkCancelSelected)
  const lastBulkCancelResult = useSelector(selectLastBulkCancelResult)
  const selectedOrders = useSelector(selectSelectedOrders)

  // Force status update selectors — available to super_admin, admin, sales_manager, and head_of_sales
  const userRoleType = useSelector(selectUserRoleType)
  const isSuperAdmin = userRoleType === 'super_admin' || userRoleType === 'admin' || userRoleType === 'sales_manager' || userRoleType === 'head_of_sales'
  const isReadOnly = isReadOnlyAdminRole(userRoleType)
  const bulkForceUpdating = useSelector(selectBulkForceUpdating)
  const canForceStatusUpdate = useSelector(selectCanForceStatusUpdate)
  const lastBulkForceUpdateResult = useSelector(selectLastBulkForceUpdateResult)
  const isAllOnPageSelectedAnyStatus = useSelector(selectIsAllOnPageSelectedAnyStatus)
  const isSomeOnPageSelectedAnyStatus = useSelector(selectIsSomeOnPageSelectedAnyStatus)

  const navigate = useNavigate()
  const searchParams = useSearch({ strict: false }) as Record<string, string | undefined>
  const openOrderNumber = searchParams?.openOrderNumber
  const pendingOpenOrderRef = useRef<string | null>(null)

  // Capture openOrderNumber from URL if present
  useEffect(() => {
    if (openOrderNumber) {
      pendingOpenOrderRef.current = openOrderNumber
    }
  }, [openOrderNumber])

  // URL filter sync - bidirectional sync between URL and Redux filter state
  const handleFilterChange = useCallback(
    (newFilter: OrdersFilter) => {
      dispatch(ordersActions.setFilter(newFilter))
    },
    [dispatch]
  )

  const { setFilter: setUrlFilter, resetFilter: resetUrlFilter, activeFilterCount } = useUrlFilterSync<OrdersFilter>({
    config: ordersFilterConfig,
    currentFilter: filter,
    onFilterChange: handleFilterChange,
    debounceMs: 300,
    syncToUrl: true,
    initFromUrl: false, // Loader handles initial URL parsing
  })

  // Local UI state
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null)
  const [selectedRowId, setSelectedRowId] = useState<string | null>(null)
  const [visibleColumns, setVisibleColumns] = useState<OrderTableColumnKey[]>(
    () => getInitialVisibleColumns(isAdmin)
  )
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false)
  const [orderToCancel, setOrderToCancel] = useState<Order | null>(null)
  const [forceStatusModalOpen, setForceStatusModalOpen] = useState(false)
  const [exportDialogOpen, setExportDialogOpen] = useState(false)
  const [exportSubmitting, setExportSubmitting] = useState(false)
  const [searchValue, setSearchValue] = useState(filter.search || '')
  const [pdfDrawerOpen, setPdfDrawerOpen] = useState(false)
  const [pdfOrder, setPdfOrder] = useState<Order | null>(null)
  const [pdfContext, setPdfContext] = useState<OrderPdfContext | null>(null)
  const [pdfPreview, setPdfPreview] = useState<OrderPdfPreview | null>(null)
  const [pdfLoading, setPdfLoading] = useState(false)
  const [pdfError, setPdfError] = useState<string | null>(null)
  const pdfPendingLoadKeyRef = useRef<string | null>(null)
  const pdfRequestIdRef = useRef(0)
  const pendingCancelOrderIdRef = useRef<string | null>(null)
  const pendingCancelOrderNumberRef = useRef<string | null>(null)
  const lastErrorToastRef = useRef<string | null>(null)
  const isNative = isNativePlatform()
  const previewMode = getPdfPreviewMode(isNative)
  const usePdfPreview = shouldUsePdfPreview(isNative)
  const exportEmailSchema = useMemo(() => createOrderExportEmailSchema(t), [t])
  const {
    register: registerExportEmail,
    handleSubmit: handleExportEmailSubmit,
    reset: resetExportEmailForm,
    setValue: setExportEmailValue,
    formState: { errors: exportEmailErrors },
  } = useForm<OrderExportEmailFormValues>({
    resolver: zodResolver(exportEmailSchema),
    defaultValues: {
      email: getInitialOrderExportEmail(userEmail),
    },
  })

  // Derive filter options from ALL variants (fetched separately — not just current page)
  const productOptions = useMemo(() => {
    const lang = i18n.language?.startsWith('ar') ? 'ar' : 'en'
    return variantFilterOptions.map(({ value, en, ar }) => ({
      value,
      label: (lang === 'ar' && ar) ? ar : en,
    }))
  }, [variantFilterOptions, i18n.language])

  const deliveryAddressOptions = useMemo(() => {
    const addressMap = new Map<string, string>()

    orders.forEach(order => {
      // Delivery address entry (exact address)
      if (order.deliveryAddressId && order.deliveryAddress) {
        if (!addressMap.has(order.deliveryAddressId)) {
          addressMap.set(order.deliveryAddressId, order.deliveryAddress)
        }
      }

      // City entry (for city-only filtering)
      if (order.cityId && order.dropoffCity) {
        if (!addressMap.has(order.cityId)) {
          addressMap.set(order.cityId, order.dropoffCity)
        }
      }
    })

    return Array.from(addressMap.entries()).map(([value, label]) => ({ value, label }))
  }, [orders])

  // Clear stale data when language changes so the loading spinner appears immediately
  const prevLangRef = useRef(i18n.language)
  useEffect(() => {
    if (prevLangRef.current !== i18n.language) {
      prevLangRef.current = i18n.language
      dispatch(ordersActions.clearOrders())
    }
  }, [dispatch, i18n.language])

  // Fetch data when filter, pagination, location context, or language changes
  const filterStatusDependency = filter.status?.join(',') ?? ''
  const filterProductsDependency = filter.products?.join(',') ?? ''
  const filterDeliveryAddressesDependency = filter.deliveryAddresses?.join(',') ?? ''
  const filterSubCustomerIdsDependency = filter.subCustomerIds?.join(',') ?? ''

  useEffect(() => {
    dispatch(ordersActions.fetchOrders(filter))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, locationsContext, i18n.language, filter.page, filter.pageSize, filterStatusDependency, filter.salesOrderId, filter.search, filter.sortBy, filter.sortOrder, filter.dateFrom, filter.dateTo, filter.deliveryDateFrom, filter.deliveryDateTo, filterProductsDependency, filterDeliveryAddressesDependency, filter.minAmount, filter.maxAmount, filter.vehicleLicensePlate, filter.driverName, filter.unknownSubCustomer, filterSubCustomerIdsDependency, filter.subCustomerPresence, filter.minQuantity, filter.maxQuantity, filter.minLoadingMinutes, filter.maxLoadingMinutes, filter.minProcessingMinutes, filter.maxProcessingMinutes])

  // Fetch all variant options once on mount for the filter drawer product selector
  useEffect(() => {
    dispatch(ordersActions.fetchVariantOptions())
  }, [dispatch])

  // Auto-open drawer for specific order from URL params
  useEffect(() => {
    if (pendingOpenOrderRef.current && orders.length > 0) {
      const targetOrderNumber = pendingOpenOrderRef.current
      const orderToOpen = orders.find(o => o.orderNumber === targetOrderNumber)
      
      if (orderToOpen) {
        setSelectedRowId(orderToOpen.id)
        setSelectedOrder(orderToOpen)
        dispatch(ordersActions.setSelectedOrder(orderToOpen))
        setDrawerOpen(true)
        
        // Clear the ref so we don't reopen it
        pendingOpenOrderRef.current = null
        
        // Ensure the param is removed from URL if it's still there
        navigate({
          search: ((prev: Record<string, string | undefined>) => ({
            ...prev,
            openOrderNumber: undefined,
          })) as any,
          replace: true,
        })
      }
    }
  }, [orders, navigate, dispatch])

  // Sync local search with filter when it changes externally
  useEffect(() => {
    setSearchValue(filter.search || '')
  }, [filter.search])

  useEffect(() => {
    if (!exportDialogOpen) return
    resetExportEmailForm({
      email: getInitialOrderExportEmail(userEmail),
    })
  }, [exportDialogOpen, resetExportEmailForm, userEmail])

  useEffect(() => {
    setVisibleColumns((currentColumns) => normalizeVisibleColumns(currentColumns, isAdmin))
  }, [isAdmin])

  useEffect(() => {
    writeStoredVisibleColumns(visibleColumns)
  }, [visibleColumns])

  useEffect(() => {
    return () => {
      if (pdfPreview?.cacheFileName) {
        void deletePdfFromCache(pdfPreview.cacheFileName)
      }
      revokePdfUrl(pdfPreview?.url)
    }
  }, [pdfPreview?.url, pdfPreview?.cacheFileName])

  useEffect(() => {
    const pendingCancelOrderId = pendingCancelOrderIdRef.current
    const pendingCancelOrderNumber = pendingCancelOrderNumberRef.current
    if (!pendingCancelOrderId || !pendingCancelOrderNumber) return
    if (!hasOrderBeenCancelled(orders, pendingCancelOrderId)) return

    toast({
      title: t('common.success'),
      description: getCancelSuccessDescription(t, pendingCancelOrderNumber),
    })

    clearPendingCancelState({ pendingCancelOrderIdRef, pendingCancelOrderNumberRef })
  }, [orders, t, toast])

  useEffect(() => {
    if (!error) {
      lastErrorToastRef.current = null
      return
    }
    if (lastErrorToastRef.current === error) return
    lastErrorToastRef.current = error

    const pendingCancelOrderNumber = pendingCancelOrderNumberRef.current
    if (pendingCancelOrderNumber) {
      toast({
        title: t('orders.cancelError'),
        description: getResolvedCancelErrorDescription(t, error, pendingCancelOrderNumber),
        variant: 'destructive',
      })
      clearPendingCancelState({ pendingCancelOrderIdRef, pendingCancelOrderNumberRef })
      return
    }

    toast({
      title: t('common.error'),
      description: error,
      variant: 'destructive',
    })
  }, [error, t, toast])

  useEffect(() => {
    if (!lastBulkCancelResult) return

    const { successCount, failureCount } = lastBulkCancelResult
    const isPartial = successCount > 0 && failureCount > 0

    if (successCount > 0 && failureCount === 0) {
      toast({
        title: t('common.success'),
        description: t('orders.bulkActions.cancelSelectedSuccess', { count: successCount }),
      })
    } else if (isPartial) {
      toast({
        title: t('orders.bulkActions.cancelSelectedPartialTitle'),
        description: t('orders.bulkActions.cancelSelectedPartialDesc', { successCount, failureCount }),
      })
    } else if (failureCount > 0) {
      toast({
        title: t('orders.bulkActions.cancelSelectedError'),
        description: t('orders.bulkActions.cancelSelectedErrorDesc'),
        variant: 'destructive',
      })
    }

    dispatch(ordersActions.clearBulkCancelResult())
  }, [dispatch, lastBulkCancelResult, t, toast])

  // Force status update result toast
  useEffect(() => {
    if (!lastBulkForceUpdateResult) return

    const { successCount, failureCount } = lastBulkForceUpdateResult
    const isPartial = successCount > 0 && failureCount > 0

    if (successCount > 0 && failureCount === 0) {
      toast({
        title: t('common.success'),
        description: t('orders.bulkActions.forceStatusUpdateSuccess', { count: successCount }),
      })
    } else if (isPartial) {
      toast({
        title: t('orders.bulkActions.forceStatusUpdatePartialTitle'),
        description: t('orders.bulkActions.forceStatusUpdatePartialDesc', { successCount, failureCount }),
      })
    } else if (failureCount > 0) {
      toast({
        title: t('orders.bulkActions.forceStatusUpdateError'),
        description: t('orders.bulkActions.forceStatusUpdateErrorDesc'),
        variant: 'destructive',
      })
    }

    dispatch(ordersActions.clearBulkForceUpdateResult())
  }, [dispatch, lastBulkForceUpdateResult, t, toast])

  // Register Copilot integrations
  useOrdersReadable()
  useOrdersCopilotActions()
  useOrdersSuggestions()

  const handleRowClick = (order: Order) => {
    setSelectedRowId(order.id)
    setSelectedOrder(order)
    dispatch(ordersActions.setSelectedOrder(order))
    setDrawerOpen(true)
  }

  const handleOrderRefreshed = useCallback((freshOrder: Order) => {
    setSelectedOrder(freshOrder)
    dispatch(ordersActions.updateOrderInList(freshOrder))
  }, [dispatch])

  const handleRowDoubleClick = (order: Order) => {
    if (selectedRowId === order.id) {
      setSelectedRowId(null)
      setSelectedOrder(null)
      setDrawerOpen(false)
    }
  }

  const handleSearchChange = (value: string) => {
    setSearchValue(value)
  }

  const handleSearchSubmit = useCallback((value: string) => {
    setUrlFilter({ search: value, page: 1 })
  }, [setUrlFilter])

  const handleStatusChange = useCallback((statuses: OrderStatus[]) => {
    setUrlFilter({ status: statuses, page: 1 })
  }, [setUrlFilter])

  const handleDateFromChange = useCallback((date: string | undefined) => {
    setUrlFilter({ dateFrom: date, page: 1 })
  }, [setUrlFilter])

  const handleDateToChange = useCallback((date: string | undefined) => {
    setUrlFilter({ dateTo: date, page: 1 })
  }, [setUrlFilter])

  const handleDeliveryDateFromChange = useCallback((date: string | undefined) => {
    setUrlFilter({ deliveryDateFrom: date, page: 1 })
  }, [setUrlFilter])

  const handleDeliveryDateToChange = useCallback((date: string | undefined) => {
    setUrlFilter({ deliveryDateTo: date, page: 1 })
  }, [setUrlFilter])

  const handleDriverNameChange = useCallback((driverName: string | undefined) => {
    setUrlFilter({ driverName: driverName?.trim() || undefined, page: 1 })
  }, [setUrlFilter])

  const handleQuantityRangeChange = useCallback((range: { min?: number; max?: number }) => {
    setUrlFilter({ minQuantity: range.min, maxQuantity: range.max, page: 1 })
  }, [setUrlFilter])

  const handleLoadingTimeRangeChange = useCallback((range: { min?: number; max?: number }) => {
    setUrlFilter({ minLoadingMinutes: range.min, maxLoadingMinutes: range.max, page: 1 })
  }, [setUrlFilter])

  const handleProcessingTimeRangeChange = useCallback((range: { min?: number; max?: number }) => {
    setUrlFilter({ minProcessingMinutes: range.min, maxProcessingMinutes: range.max, page: 1 })
  }, [setUrlFilter])

  const handleSortChange = useCallback((sortBy: OrdersSortBy, sortOrder: OrdersSortOrder) => {
    setUrlFilter({ sortBy, sortOrder })
  }, [setUrlFilter])

  const handleVisibleColumnsChange = useCallback((columns: OrderTableColumnKey[]) => {
    setVisibleColumns(normalizeVisibleColumns(columns, isAdmin))
  }, [isAdmin])

  const handleResetColumns = useCallback(() => {
    setVisibleColumns(getDefaultVisibleColumns(isAdmin))
  }, [isAdmin])

  const handleClearFilters = useCallback(() => {
    setSearchValue('')
    resetUrlFilter()
  }, [resetUrlFilter])

  // Stats cards are single-select quick filters (distinct from multi-select drawer filters)
  const handleStatusFilter = (status: string) => {
    if (status === 'all') {
      setUrlFilter({ status: [], page: 1 })
      return
    }

    const statusValue = status as OrderStatus
    const isSameSingleStatus = filter.status?.length === 1 && filter.status[0] === statusValue

    setUrlFilter({ status: isSameSingleStatus ? [] : [statusValue], page: 1 })
  }

  const handlePageChange = (page: number) => {
    setUrlFilter({ page })
  }

  const handlePageSizeChange = (pageSize: number) => {
    setUrlFilter({ pageSize, page: 1 })
  }

  const handleRequestCancelOrder = (order: Order) => {
    if (!isCancelableOrder(order, userRoleType ?? undefined)) return
    setOrderToCancel(order)
    setCancelDialogOpen(true)
  }

  const handleConfirmCancelOrder = () => {
    if (!orderToCancel) return
    const order = orderToCancel
    pendingCancelOrderIdRef.current = order.id
    pendingCancelOrderNumberRef.current = getOrderNumberDisplay(order)
    dispatch(ordersActions.cancelOrder({ orderId: order.id, reason: t('orders.cancelReasonUserRequested') }))
    setCancelDialogOpen(false)
    setOrderToCancel(null)
  }

  const handleCancelDialogOpenChange = (open: boolean) => {
    setCancelDialogOpen(open)
    if (!open) {
      setOrderToCancel(null)
    }
  }

  const handleOpenExportDialog = useCallback(() => {
    setExportEmailValue('email', getInitialOrderExportEmail(userEmail))
    setExportDialogOpen(true)
  }, [setExportEmailValue, userEmail])

  const handleExportDialogOpenChange = useCallback((open: boolean) => {
    setExportDialogOpen(open)
    if (!open) {
      resetExportEmailForm({
        email: getInitialOrderExportEmail(userEmail),
      })
    }
  }, [resetExportEmailForm, userEmail])

  const submitOrderExport = handleExportEmailSubmit(async (values) => {
    try {
      setExportSubmitting(true)
      const selectedOrderIdsForExport = Array.from(new Set(selectedOrderIds.filter(Boolean)))

      toast({
        title: t('orders.export.exportingTitle', { defaultValue: 'Exporting...' }),
        description: getExportTargetDescription(t, selectedOrderIdsForExport.length),
      })

      const result = await ordersApi.exportAll({
        filter,
        email: values.email.trim(),
        selectedOrderIds: selectedOrderIdsForExport,
        includeTenantColumn: isAdmin,
        locale: i18n.language,
      })

      if (result.mode === 'email') {
        toast({
          title: t('orders.export.exportEmailedTitle', { defaultValue: 'Export will be emailed' }),
          description: t('orders.export.exportEmailedDescription', {
            email: result.email,
            count: result.count,
            defaultValue: `We will send ${result.count} orders to ${result.email}`,
          }),
        })
        handleExportDialogOpenChange(false)
        return
      }

      exportToXlsx({
        fileName: result.fileName || 'orders',
        sheetName: 'Orders',
        columns: getOrderExportColumns(isAdmin),
        rows: result.orders,
      })

      toast({
        title: t('orders.export.exportDownloadedTitle', { defaultValue: 'Export downloaded' }),
        description: t('orders.export.exportDownloadedDescription', {
          count: result.count,
          defaultValue: `Downloaded ${result.count} orders`,
        }),
      })
      handleExportDialogOpenChange(false)
    } catch (error) {
      console.error('Export failed:', error)
      toast({
        title: t('orders.export.exportFailedTitle', { defaultValue: 'Export failed' }),
        description: isForbiddenError(error)
          ? t('orders.export.exportForbidden')
          : t('orders.export.exportFailed', { defaultValue: 'Could not export orders' }),
        variant: 'destructive',
      })
    } finally {
      setExportSubmitting(false)
    }
  })

  const pdfStateHandlers: PdfPreviewStateHandlers = {
    setPdfContext,
    setPdfPreview,
    setPdfDrawerOpen,
    setPdfLoading,
    setPdfError,
  }

  const handlePdfDrawerOpenChange = (open: boolean) => {
    setPdfDrawerOpen(open)
    if (!open) {
      resetPdfPreviewState(pdfPreview, pdfStateHandlers, { preservePreview: true })
      setPdfOrder(null)
    }
  }

  const handleDownloadPDF = async (order: Order) => {
    if (usePdfPreview) {
      setPdfOrder(order)
      await openOrderPdfPreview({
        order,
        t,
        toast,
        currentPreview: pdfPreview,
        currentContext: pdfContext,
        currentError: pdfError,
        isLoading: pdfLoading,
        pendingLoadKeyRef: pdfPendingLoadKeyRef,
        requestRef: pdfRequestIdRef,
        handlers: pdfStateHandlers,
      })
      return
    }

    await downloadOrderPdf({ order, t, toast })
  }

  const handleDownloadDeliveryNote = async (order: Order) => {
    if (usePdfPreview) {
      setPdfOrder(order)
      await openOrderDeliveryNotePreview({
        order,
        t,
        toast,
        currentPreview: pdfPreview,
        currentContext: pdfContext,
        currentError: pdfError,
        isLoading: pdfLoading,
        pendingLoadKeyRef: pdfPendingLoadKeyRef,
        requestRef: pdfRequestIdRef,
        handlers: pdfStateHandlers,
      })
      return
    }

    await downloadOrderDeliveryNote({ order, t, toast })
  }

  const handlePdfShare = () => {
    sharePdfPreview({ preview: pdfPreview, t, toast })
  }

  const handlePdfRetry = () => {
    if (!pdfOrder || !pdfContext) return
    if (pdfContext.kind === 'delivery_note') {
      void openOrderDeliveryNotePreview({
        order: pdfOrder,
        t,
        toast,
        currentPreview: pdfPreview,
        currentContext: pdfContext,
        currentError: pdfError,
        isLoading: pdfLoading,
        pendingLoadKeyRef: pdfPendingLoadKeyRef,
        requestRef: pdfRequestIdRef,
        handlers: pdfStateHandlers,
      })
      return
    }
    void openOrderPdfPreview({
      order: pdfOrder,
      t,
      toast,
      currentPreview: pdfPreview,
      currentContext: pdfContext,
      currentError: pdfError,
      isLoading: pdfLoading,
      pendingLoadKeyRef: pdfPendingLoadKeyRef,
      requestRef: pdfRequestIdRef,
      handlers: pdfStateHandlers,
    })
  }

  const handlePdfPreviewError = (message?: string) => {
    setPdfPreviewError({
      message,
      handlers: pdfStateHandlers,
      t,
    })
  }

  // Multi-select handlers
  const handleToggleOrderSelection = useCallback((orderId: string) => {
    dispatch(ordersActions.toggleOrderSelection(orderId))
  }, [dispatch])

  const handleSelectAllOnPage = useCallback(() => {
    const pageOrderIds = getSelectableOrderIds(orders)
    dispatch(ordersActions.selectAllOnPage(pageOrderIds))
  }, [dispatch, orders])

  const handleDeselectAllOnPage = useCallback(() => {
    const pageOrderIds = getSelectableOrderIds(orders)
    dispatch(ordersActions.deselectAllOnPage(pageOrderIds))
  }, [dispatch, orders])

  const handleSelectAllAcrossPages = useCallback(() => {
    dispatch(ordersActions.selectAllAcrossPages())
  }, [dispatch])

  const handleClearSelection = useCallback(() => {
    dispatch(ordersActions.clearSelection())
  }, [dispatch])

  const handleSelectAllAnyStatus = useCallback(() => {
    const allIds = orders.map(o => o.id)
    dispatch(ordersActions.selectAllAnyStatus(allIds))
  }, [dispatch, orders])

  const handleDeselectAllAnyStatus = useCallback(() => {
    dispatch(ordersActions.clearSelection())
  }, [dispatch])

  const handleToggleSelectAll = useCallback(() => {
    if (isSuperAdmin) {
      if (isAllOnPageSelectedAnyStatus) {
        handleDeselectAllAnyStatus()
      } else {
        handleSelectAllAnyStatus()
      }
    } else {
      if (isAllOnPageSelected) {
        handleDeselectAllOnPage()
      } else {
        handleSelectAllOnPage()
      }
    }
  }, [
    isSuperAdmin,
    isAllOnPageSelected,
    isAllOnPageSelectedAnyStatus,
    handleDeselectAllOnPage,
    handleSelectAllOnPage,
    handleDeselectAllAnyStatus,
    handleSelectAllAnyStatus,
  ])

  const handleDownloadSelectedPDFs = useCallback(() => {
    return downloadBulkOrderPdfs({
      selectedOrderIds,
      usePdfPreview,
      t,
      toast,
      dispatch,
    })
  }, [dispatch, selectedOrderIds, t, toast, usePdfPreview])

  const handleBulkCancelSelected = useCallback(() => {
    if (selectedOrderIds.length === 0) return
    dispatch(ordersActions.bulkCancelOrders({
      orderIds: selectedOrderIds,
      reason: t('orders.cancelReasonUserRequested'),
    }))
  }, [dispatch, selectedOrderIds, t])

  const handleOpenForceStatusModal = useCallback(() => {
    if (selectedOrderIds.length === 0) return
    setForceStatusModalOpen(true)
  }, [selectedOrderIds.length])

  const handleForceStatusConfirm = useCallback((payload: ForceStatusUpdatePayload) => {
    dispatch(ordersActions.bulkForceUpdateStatus({
      orderIds: selectedOrderIds,
      newStatus: payload.newStatus,
      reason: payload.reason,
      incidentRef: payload.incidentRef,
      affectedOpsNote: payload.affectedOpsNote,
      forceJobId: payload.forceJobId,
    }))
    setForceStatusModalOpen(false)
  }, [dispatch, selectedOrderIds])

  if (loading && orders.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <OrdersHeader
        isRTL={isRTL}
        onExport={handleOpenExportDialog}
      />

      <OrdersStats
        stats={stats}
        activeFilter={filter.status?.length === 1 ? filter.status[0] : 'all'}
        onStatusFilter={handleStatusFilter}
      />

      <OrdersToolbarV2
        searchValue={searchValue}
        onSearchChange={handleSearchChange}
        onSearchSubmit={handleSearchSubmit}
        statusFilter={filter.status || []}
        onStatusChange={handleStatusChange}
        productFilter={filter.products || []}
        productOptions={productOptions}
        deliveryAddressFilter={filter.deliveryAddresses || []}
        deliveryAddressOptions={deliveryAddressOptions}
        vehicleLicensePlateFilter={filter.vehicleLicensePlate}
        driverNameFilter={filter.driverName}
        unknownSubCustomerFilter={filter.unknownSubCustomer}
        subCustomerIdsFilter={filter.subCustomerIds}
        subCustomerPresenceFilter={filter.subCustomerPresence}
        onDriverNameChange={handleDriverNameChange}
        minQuantity={filter.minQuantity}
        maxQuantity={filter.maxQuantity}
        onQuantityRangeChange={handleQuantityRangeChange}
        minLoadingMinutes={filter.minLoadingMinutes}
        maxLoadingMinutes={filter.maxLoadingMinutes}
        onLoadingTimeRangeChange={handleLoadingTimeRangeChange}
        minProcessingMinutes={filter.minProcessingMinutes}
        maxProcessingMinutes={filter.maxProcessingMinutes}
        onProcessingTimeRangeChange={handleProcessingTimeRangeChange}
        dateFrom={filter.dateFrom}
        dateTo={filter.dateTo}
        onDateFromChange={handleDateFromChange}
        onDateToChange={handleDateToChange}
        deliveryDateFrom={filter.deliveryDateFrom}
        deliveryDateTo={filter.deliveryDateTo}
        onDeliveryDateFromChange={handleDeliveryDateFromChange}
        onDeliveryDateToChange={handleDeliveryDateToChange}
        columnOptions={toolbarColumnOptions}
        visibleColumns={visibleColumns}
        onVisibleColumnsChange={handleVisibleColumnsChange}
        onResetColumns={handleResetColumns}
        isLoading={loading}
        activeFilterCount={activeFilterCount}
        onClearFilters={handleClearFilters}
        onApplyAllFilters={(filters) => {
          // Batch update all filters at once via URL sync
          setUrlFilter({
            status: filters.status,
            products: filters.products,
            deliveryAddresses: filters.deliveryAddresses,
            dateFrom: filters.dateFrom,
            dateTo: filters.dateTo,
            deliveryDateFrom: filters.deliveryDateFrom,
            deliveryDateTo: filters.deliveryDateTo,
            vehicleLicensePlate: filters.vehicleLicensePlate,
            driverName: filters.driverName?.trim() || undefined,
            unknownSubCustomer: filters.unknownSubCustomer || undefined,
            subCustomerIds: filters.subCustomerIds.length > 0 ? filters.subCustomerIds : undefined,
            subCustomerPresence: filters.subCustomerPresence,
            minQuantity: filters.minQuantity,
            maxQuantity: filters.maxQuantity,
            minLoadingMinutes: filters.minLoadingMinutes,
            maxLoadingMinutes: filters.maxLoadingMinutes,
            minProcessingMinutes: filters.minProcessingMinutes,
            maxProcessingMinutes: filters.maxProcessingMinutes,
            page: 1,
          })
        }}
        // Bulk selection props
        selectedCount={selectedCount}
        totalOrdersCount={pagination.total}
        isAllOnPageSelected={isAllOnPageSelected}
        isSomeOnPageSelected={isSomeOnPageSelected}
        onSelectAllOnPage={handleSelectAllOnPage}
        onDeselectAllOnPage={handleDeselectAllOnPage}
        onSelectAllAcrossPages={handleSelectAllAcrossPages}
        onClearSelection={handleClearSelection}
        onDownloadSelectedPDFs={handleDownloadSelectedPDFs}
        isBulkDownloading={bulkDownloading}
        canDownloadBulkPDF={canDownloadBulkPDF}
        onBulkCancelSelected={handleBulkCancelSelected}
        canBulkCancelSelected={canBulkCancelSelected && !isReadOnly}
        isBulkCancelling={bulkCancelling}
        // Super-admin force status props
        isSuperAdmin={isSuperAdmin}
        isAllOnPageSelectedAnyStatus={isAllOnPageSelectedAnyStatus}
        isSomeOnPageSelectedAnyStatus={isSomeOnPageSelectedAnyStatus}
        onSelectAllAnyStatus={handleSelectAllAnyStatus}
        onDeselectAllAnyStatus={handleDeselectAllAnyStatus}
        onForceStatusUpdate={handleOpenForceStatusModal}
        canForceStatusUpdate={canForceStatusUpdate}
        isBulkForceUpdating={bulkForceUpdating}
      />

      {orders.length === 0 ? (
        <OrdersEmptyState />
      ) : (
        <OrdersTable
          orders={orders}
          selectedRowId={selectedRowId}
          isRTL={isRTL}
          showTenantColumn={isAdmin}
          visibleColumns={visibleColumns}
          sortBy={filter.sortBy}
          sortOrder={filter.sortOrder}
          onSortChange={handleSortChange}
          onRowClick={handleRowClick}
          onRowDoubleClick={handleRowDoubleClick}
          onCancel={handleRequestCancelOrder}
          onDownloadPDF={handleDownloadPDF}
          // Checkbox selection props
          showCheckboxes={true}
          selectedOrderIds={selectedOrderIds}
          onToggleOrderSelection={handleToggleOrderSelection}
          onToggleSelectAll={handleToggleSelectAll}
          onToggleSelectAllAnyStatus={isSuperAdmin ? handleToggleSelectAll : undefined}
          isAllOnPageSelected={isAllOnPageSelected}
          isSomeOnPageSelected={isSomeOnPageSelected}
          isAllOnPageSelectedAnyStatus={isAllOnPageSelectedAnyStatus}
          isSomeOnPageSelectedAnyStatus={isSomeOnPageSelectedAnyStatus}
        />
      )}

      {/* Pagination */}
      {orders.length > 0 && (
        <PaginationControls
          page={pagination.page}
          pageSize={pagination.pageSize}
          totalItems={pagination.total}
          totalPages={pagination.pageCount}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          pageSizes={[10, 20, 50, 100]}
          isRTL={isRTL}
        />
      )}

      <Suspense fallback={null}>
        <OrderDetailDrawer
          order={selectedOrder}
          open={drawerOpen}
          onOpenChange={(open) => {
            setDrawerOpen(open)
            if (!open) setSelectedRowId(null)
          }}
          onViewSalesOrder={drawerNav?.openSalesOrderDrawer}
          onCancel={handleRequestCancelOrder}
          onOrderRefreshed={handleOrderRefreshed}
          onDownloadPdf={handleDownloadPDF}
          onDownloadDeliveryNote={handleDownloadDeliveryNote}
          preferDeliveryNotePreview={usePdfPreview}
          canJoinQueue={canJoinQueue}
          onJoinQueue={onJoinQueue}
        />
      </Suspense>

      <Suspense fallback={null}>
        <OrderPdfDetailDrawer
          open={pdfDrawerOpen}
          onOpenChange={handlePdfDrawerOpenChange}
          orderNumber={pdfContext?.orderNumber ?? ''}
          documentKind={pdfContext?.kind ?? 'order_confirmation'}
          fileName={pdfPreview?.fileName}
          fileSize={pdfPreview?.fileSize}
          pdfUrl={pdfPreview?.url}
          pdfBlob={pdfPreview?.blob}
          previewMode={previewMode}
          isLoading={pdfLoading}
          error={pdfError}
          onShare={handlePdfShare}
          onRetry={handlePdfRetry}
          onPreviewError={handlePdfPreviewError}
        />
      </Suspense>

      <AlertDialog open={cancelDialogOpen} onOpenChange={handleCancelDialogOpenChange}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('orders.cancelConfirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('orders.cancelConfirmDescription', {
                orderNumber: orderToCancel ? getOrderNumberDisplay(orderToCancel) : '',
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmCancelOrder}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t('orders.cancel')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={exportDialogOpen} onOpenChange={handleExportDialogOpenChange}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {t('orders.export.emailPromptTitle', { defaultValue: 'Where should we send the export?' })}
            </DialogTitle>
            <DialogDescription>
              {t('orders.export.emailPromptDescription', {
                defaultValue: 'Enter the email address for this export. Small exports download immediately after submit, and larger exports are sent by email.',
              })}
            </DialogDescription>
          </DialogHeader>

          <form id="order-export-email-form" onSubmit={submitOrderExport} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="order-export-email">
                {t('orders.export.emailLabel', { defaultValue: 'Email address' })}
              </Label>
              <Input
                id="order-export-email"
                type="email"
                autoFocus
                placeholder={t('orders.export.emailPlaceholder', { defaultValue: 'name@example.com' })}
                {...registerExportEmail('email')}
              />
              {exportEmailErrors.email?.message && (
                <p className="text-xs text-destructive">
                  {exportEmailErrors.email.message}
                </p>
              )}
            </div>
          </form>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleExportDialogOpenChange(false)}
              disabled={exportSubmitting}
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              form="order-export-email-form"
              disabled={exportSubmitting}
            >
              {exportSubmitting && <Spinner className="me-2 h-4 w-4" />}
              {t('common.export')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Force Status Update Modal — super_admin only */}
      {isSuperAdmin && (
        <ForceStatusUpdateModal
          open={forceStatusModalOpen}
          onClose={() => setForceStatusModalOpen(false)}
          selectedOrders={selectedOrders}
          onConfirm={handleForceStatusConfirm}
        />
      )}
    </div>
  )
}
